import React, { useCallback, useRef, useState } from 'react'
import {
  Card,
  Form,
  FormLayout,
  Page,
  Modal,
  Button,
  Spinner,
  SkeletonBodyText, Link, FooterHelp, Banner
} from '@shopify/polaris'
import { useDispatch, useSelector } from 'react-redux'
import isEqual from 'react-fast-compare'
import axios from 'axios'
import { useHistory } from 'react-router-dom'

import { mapOrder, arrayMove } from '../../libs/utils'

import {
  actionSetSelectedItem,
  actionUpdateItemField,
  actionSetToastMessage,
  actionSetToastError,
  actionToggleToastActive
} from '../../store/actions'

import useShouldSendRequest from '../../hooks/useShouldSendRequest'

import TextInputComponent from '../../components/text-input'
import SelectComponent from '../../components/select-input'
import MapperListComponent from '../../components/mapper-list/mapper-list'
import ItemListComponent from '../../components/item-list'
import ContextualSaveBarComponent from '../../components/custom-contextual-save-bar'
import PromptAlertComponent from '../../components/prompt-alert'

const EditGalleryComponent = (props) => {
  const history = useHistory()
  const editingId = props.match ? props.match.params.id : false
  const dispatch = useDispatch()
  const activeItem = useSelector(state => state.activeItem)
  const originalItem = useRef({...activeItem})
  const [modalActive, setModalActive] = useState(false)
  const [loading, setLoading] = useState(false)
  const shouldLoading = useShouldSendRequest(
    `/api/v2/admin/gallery/${editingId}`,
    editingId && !Object.keys(activeItem).length,
    (data) => {
      originalItem.current = data
    }
  )

  const mapperOrder = activeItem.mapper_order ? activeItem.mapper_order.split(',').map(Number) : []
  const orderedMappers = activeItem.mappers ? mapOrder(activeItem.mappers, mapperOrder, 'id') : []

  const handleModalChange = useCallback(() => setModalActive(!modalActive), [modalActive])
  const activator = <Button primary onClick={handleModalChange}>Add or Remove mappers</Button>

  const isMapperAdded = (id) => {
    return (activeItem.mappers || []).filter(item => parseInt(item.id) === parseInt(id)).length > 0
  }

  const handleSelectedMappers = (item) => {
    const addedMappers = activeItem.mappers || []
    const updatedMappers = (isMapperAdded(item.id)) ?
      [...addedMappers].filter(mapper => mapper.id !== item.id) :
      addedMappers.concat([item])
    dispatch(actionUpdateItemField('mappers', updatedMappers))
    const mapperOrders = updatedMappers.map(mapper => mapper.id).join(',')
    dispatch(actionUpdateItemField('mapper_order', mapperOrders))
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const newMappers = arrayMove([...activeItem.mappers], oldIndex, newIndex)
    dispatch(actionUpdateItemField('mappers', newMappers))
    const mapperOrders = newMappers.map(mapper => mapper.id).join(',')
    dispatch(actionUpdateItemField('mapper_order', mapperOrders))
  }

  const saveAction = (shouldRedirectToGetCode = false) => {
    setLoading(true)
    const galleryData = {
      ...activeItem,
      mappers: [],
      mapper_ids: ({ ...activeItem }.mappers || []).map(mapper => mapper.id)
    }
    let endpoint, data
    if (editingId) {
      endpoint = '/api/v2/admin/galleries/save'
      data = {
        id: editingId,
        gallery: galleryData
      }
    } else {
      endpoint = '/api/v2/admin/galleries/new'
      data = {
        gallery: galleryData
      }
    }
    axios.post(endpoint, data)
      .then((res) => {
        originalItem.current = activeItem
        setLoading(false)
        if (shouldRedirectToGetCode) {
          history.push(`/galleries/${res.data.id}/code`)
        }
      })
      .catch((e) => {
        console.log(e)
      })
  }

  return (
    <div>
      <Page fullWidth>
        <Banner status="info">
          <p>Please note that because modern browsers cache the javascript files, it may take some time for your storefront to reflect saved changes, however, you can always see the new changes in the private/incognito window of your browser as private window is never cached by the browser.</p>
        </Banner>
      </Page>
      <Page
        fullWidth
        breadcrumbs={[{
          content: 'Galleries',
          url: '/galleries'
        }]}
        title={editingId ? 'Edit gallery' : 'Add gallery'}
      >
        {shouldLoading && <SkeletonBodyText />}
        {!shouldLoading && <div className={loading ? 'loading-wrapper' : null}>
          {loading && <Spinner accessibilityLabel="Saving data" size="large" color="teal" />}
          {!isEqual(originalItem.current, activeItem) && <ContextualSaveBarComponent
            onDiscardAction={() => {
              dispatch(actionSetSelectedItem(originalItem.current))
            }}
            onSaveAction={() => {
              saveAction(!editingId)
              dispatch(actionSetToastMessage(editingId ? 'Gallery saved' : 'Gallery created'))
              dispatch(actionSetToastError(false))
              dispatch(actionToggleToastActive(true))
            }}
          />}
          <PromptAlertComponent when={!isEqual(originalItem.current, activeItem)}/>
          <Card sectioned>
            <Form noValidate onSubmit={() => {}}>
              <FormLayout>
                <TextInputComponent
                  name='name'
                  value={activeItem.name}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                  label='Gallery name'
                />
                <SelectComponent
                  label='Gallery type'
                  options={[
                    { label: 'Grid', value: 'grid' },
                    { label: 'Masonry', value: 'masonry' }
                  ]}
                  name='gallery_type'
                  value={activeItem.gallery_type}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                />
                <SelectComponent
                  label='Gallery columns'
                  options={[
                    { label: '1', value: '1' },
                    { label: '2', value: '2' },
                    { label: '3', value: '3' },
                    { label: '4', value: '4' }
                  ]}
                  name='grid_columns'
                  value={activeItem.grid_columns}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                />
                <TextInputComponent
                  name='column_spacing'
                  value={activeItem.column_spacing}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                  label='Column spacing'
                />
                {activator}
                <Modal
                  activator={activator}
                  open={modalActive}
                  title="Select mapper"
                  onClose={handleModalChange}
                >
                  <ItemListComponent
                    onClick={(item) => {
                      handleSelectedMappers(item)
                    }}
                    customShortcutActions={(item => {
                      return [
                        {
                          content: isMapperAdded(item.id) ? 'Remove' : 'Add',
                          onAction: () => handleSelectedMappers(item)
                        }
                      ]
                    })}
                    resourceName={{
                      singular: 'mapper',
                      plural: 'mappers'
                    }}
                    endpoint={`/api/v2/admin/mappers`}
                  />
                </Modal>
                <MapperListComponent list={orderedMappers} onSortEnd={onSortEnd}/>
              </FormLayout>
            </Form>
          </Card>
        </div>}
        <FooterHelp>
          Learn more about<Link url="/docs#/gallery-settings" external> gallery settings</Link>
        </FooterHelp>
      </Page>
    </div>
  )
}

export default EditGalleryComponent
